import { deleteCookie } from '@/utils/cookies';
import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocalStorage } from './useLocalStorage';
import { toast } from '@/components/Toast/toastUtils';
import { clearPerfAccessToken, getPerfAccessToken } from '@/auth/perfTokenAuth';
import { trackEvent } from '@/analytics';
import { recordApiPerformance } from '@/analytics/apiPerformance';
import { useCallback, useMemo } from 'react';
import { forgetSupportProvider } from '@/services/vision-support';

const FLASH_URL = import.meta.env.VITE_FLASH_URL;
const API_URL = import.meta.env.VITE_CYCLONE_API_URL || 'INSERT_CYCLONE_API_URL';

// ── Refresh forzado post-401: single-flight ──
// Con refresh token ROTATIVO, dos requests que pegan 401 a la vez y refrescan en paralelo
// disparan la reuse-detection de Auth0 (el segundo usa el token ya rotado) y Auth0 revoca
// la familia entera → sesión muerta → logout a mitad del setup. Sucede solo en celular
// (pestañas duplicadas/restauradas comparten el localstorage pero no el dedupe in-memory
// de auth0-spa-js). Compartimos UNA promesa de refresh por vez, a nivel módulo.
// Kill-switch: VITE_AUTH_401_DEDUPE=0 vuelve al comportamiento anterior (refresh por request).
const AUTH_401_DEDUPE = import.meta.env.VITE_AUTH_401_DEDUPE !== '0';
let inflightFreshToken: Promise<string> | null = null;

const getFreshTokenOnce = (getter: () => Promise<string>): Promise<string> => {
  if (!AUTH_401_DEDUPE) return getter();
  if (!inflightFreshToken) {
    inflightFreshToken = getter().finally(() => {
      inflightFreshToken = null;
    });
  }
  return inflightFreshToken;
};

/** Los textos con los que rechaza un 404 o un 5xx cuando no viaja el del servidor. */
export const NOT_FOUND_MESSAGE = 'Not found.';
export const SERVICE_UNAVAILABLE_MESSAGE =
  'El servicio no está disponible. Volvé a intentar en unos segundos.';

// Telemetría de baja cardinalidad: 'me/vendor/bookings/123' → 'me/vendor/bookings/:id'
const sanitizeEndpoint = (endpoint: string): string => endpoint.split('?')[0].replace(/\d+/g, ':id');

interface useClientI {
  client: ClientI;
}

interface FetchConfigI {
  method: string;
  body: any;
  headers: any;
  signal?: AbortSignal;
}

// `fetch` no tiene timeout propio: si la conexión se traga los paquetes en vez de
// cortar, la promesa queda PENDIENTE para siempre. El 12/ago eso dejó a vendors
// mirando el spinner del arranque durante horas — sin error, sin reintento y sin
// salida, porque el Router espera a que la sesión resuelva.
//
// Los GET del shell terminan muy por debajo de 12s cuando la ruta está sana. Las
// escrituras reciben 30s porque el máximo medido de un POST de reserva fue 19,5s.
// Multipart y JSON grande mantienen su excepción más abajo para no cortar uploads.
const READ_TIMEOUT_MS = 12_000;
const WRITE_TIMEOUT_MS = 30_000;

// Umbral para considerar que un body JSON es en realidad una subida de archivos.
const LARGE_BODY_BYTES = 512 * 1024;

// `AbortSignal.timeout` recién existe desde Chrome 103 / Safari 16 (2022), y el build
// no declara browserslist: Vite apunta a Safari 14+ por defecto y NO polyfillea APIs,
// solo sintaxis. En 7 días de datos el navegador más viejo del panel es Safari 16 /
// Chrome 109 (ambos lo soportan), pero ~1.100 usuarios entran por webviews cuya
// versión no se puede leer. Si la API no está, degradamos al comportamiento de hoy
// —sin timeout— en vez de tirar un TypeError que rompería TODOS los requests.
const timeoutSignal = (ms: number): AbortSignal | undefined =>
  typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
    ? AbortSignal.timeout(ms)
    : undefined;

type EndpointRequest = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface ClientI {
  <T>(
    endpoint: string,
    type?: EndpointRequest,
    options?: {
      isAuthRequired?: boolean;
      data?: any;
      isMultipartForm?: boolean;
      // Para endpoints donde un 404 es un estado esperado y no un error que el
      // usuario deba ver. El request igual rechaza: sólo se calla el toast.
      suppressNotFoundToast?: boolean;
      // Para queries accesorias (prefetch, enriquecimiento) o acciones cuyo formulario
      // muestra su propio error persistente y permite reintentar. El request igual
      // rechaza; sólo se evita el toast duplicado. Nunca silenciar una acción sin
      // mostrar su fallo al usuario en la superficie que la inició.
      suppressNetworkToast?: boolean;
      timeoutMs?: number;
      // El 404 y los 5xx rechazan con un texto genérico. Con esto viaja el `message` que
      // mandó el servidor (si mandó uno), para endpoints que explican el error en castellano.
      keepErrorMessage?: boolean;
    }
  ): Promise<T>;
}

const getProcessedData = (data: any, isMultipartForm: boolean): FormData | string => {
  if (isMultipartForm) {
    const formData = new FormData();

    for (const name in data) {
      if (data[name] instanceof File) {
        if (name.startsWith('video_class_')) {
          formData.append(name, data[name]);
        } else if (name === 'cover_url') {
          formData.append('cover_image', data[name]);
        } else {
          formData.append('files', data[name]);
        }
      } else if (name === 'image_gallery') {
        formData.append(
          name,
          new Blob([JSON.stringify(data[name])], {
            type: 'application/json'
          })
        );
      } else if (typeof data[name] === 'object' && data[name] !== null) {
        formData.append(name, JSON.stringify(data[name]));
      } else if (data[name] !== null && data[name] !== undefined) {
        formData.append(name, data[name]);
      }
    }

    return formData;
  }
  return JSON.stringify(data);
};

// `silent` = la query es accesoria o tiene feedback propio (ver `suppressNetworkToast`). El error se propaga
// igual: lo único que cambia es que no se pinta un cartel por algo que el usuario no
// pidió. Sin esto, un prefetch que se pasa del timeout se ve idéntico a que le haya
// fallado la acción que acaba de hacer — el 3/9/2026 hallsalon vio cinco carteles
// apilados de "No pudimos conectar con Ágora" barriendo la agenda con el mouse, y uno
// más al lado de "Pago total confirmado exitosamente".
const makeNetworkErrorHandler =
  (silent: boolean) =>
  (error: Error): never => {
    if (error.name === 'AbortError') {
      console.debug('Request was aborted');
      throw error;
    }

    if (!navigator.onLine) {
      if (!silent) {
        toast.error('No hay conexión a internet. Por favor, revisa tu conexión y vuelve a intentarlo.');
      }
      throw error;
    }

    // `AbortSignal.timeout` aborta con un DOMException de nombre 'TimeoutError'
    // (NO 'AbortError', que es el de una cancelación explícita). Sin esta rama caía
    // al throw pelado de abajo: sin toast, y el usuario volvía a mirar una pantalla
    // muda — justo lo que este timeout viene a evitar.
    if (error.name === 'TimeoutError') {
      if (!silent) toast.error('No pudimos conectar con Ágora. Volvé a intentar en unos segundos.');
      throw error;
    }

    if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
      if (!silent) toast.error('No pudimos conectar con Ágora. Volvé a intentar en unos segundos.');
      throw error;
    }

    throw error;
  };

// Una sola salida aunque la ráfaga de queries reciba el 403 a la vez.
let godModeRevoked = false;

export const useClient = (): useClientI => {
  const { logout, getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const vendorUsernameGodMode = useLocalStorage<string | null>('vendorUsernameGodMode', null);

  const logOutUser = useCallback(() => {
    // Sesión perdida (401): el chat de Vision no se puede cerrar en Cyclone sin token; se olvida
    // acá para que el login vuelva a tener Intercom (ver forgetSupportProvider).
    forgetSupportProvider();
    if (getPerfAccessToken()) {
      clearPerfAccessToken();
      deleteCookie('authInfo');
      window.location.assign('/login');
      return;
    }

    logout({ logoutParams: { returnTo: FLASH_URL } });
    deleteCookie('authInfo');
  }, [logout]);

  const client: ClientI = useCallback(
    async (
      endpoint,
      type = 'GET',
      {
        data,
        isAuthRequired,
        isMultipartForm,
        suppressNotFoundToast,
        suppressNetworkToast,
        timeoutMs,
        keepErrorMessage
      } = {}
    ) => {
      const onNetworkError = makeNetworkErrorHandler(!!suppressNetworkToast);
      const requestStartedAt = performance.now();
      const pageVisibilityAtStart = document.visibilityState;
      let pageHiddenDuringRequest = false;
      let authDurationMs = 0;
      let networkDurationMs = 0;
      let parseDurationMs = 0;
      let latestResponse: Response | undefined;
      let retried401 = false;
      let errorName: string | undefined;
      const markPageHidden = () => {
        if (document.visibilityState === 'hidden') pageHiddenDuringRequest = true;
      };
      document.addEventListener('visibilitychange', markPageHidden);

      // Body construido UNA vez y reutilizado en el reintento post-401 (FormData y
      // strings se pueden re-enviar sin problema en fetch de browser).
      const body = data ? getProcessedData(data, !!isMultipartForm) : undefined;

      // Una subida no siempre es multipart: si falla la subida directa de imágenes,
      // la galería viaja en base64 DENTRO del JSON — un PUT de hasta ~13 MB a
      // `me/vendor/storefront`. Cortarlo a los 20s le borraría las fotos al vendor sin
      // decirle nada, y el reintento tampoco lo salvaría porque el payload no adelgaza.
      // Medimos el tamaño del body en vez de listar endpoints: así cubre también los
      // que vengan después sin que nadie se acuerde de agregarlos acá.
      const isLargeUpload = typeof body === 'string' && body.length > LARGE_BODY_BYTES;
      const effectiveTimeoutMs = timeoutMs ?? (type === 'GET' ? READ_TIMEOUT_MS : WRITE_TIMEOUT_MS);

      const doFetch = async (forceFreshToken: boolean) => {
        const authStartedAt = performance.now();
        let authHeader = {};
        try {
          if (isAuthRequired) {
            const token =
              getPerfAccessToken() ||
              (forceFreshToken
                ? await getFreshTokenOnce(() => getAccessTokenSilently({ cacheMode: 'off' }))
                : await getAccessTokenSilently());
            authHeader = { Authorization: `Bearer ${token}` };
          }
        } finally {
          authDurationMs += performance.now() - authStartedAt;
        }

        const config: FetchConfigI = {
          method: type,
          body,
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Frontend-Page-URL': window.location.href,
            ...(vendorUsernameGodMode.storedValue && { 'X-God-Mode': vendorUsernameGodMode.storedValue }),
            ...authHeader
          },
          // Las subidas multipart quedan SIN timeout a propósito: una galería de
          // imágenes o un video de clase tardan minutos en un celular, y cortarlas
          // a los 20s rompería algo que hoy funciona. El signal se crea acá adentro
          // (no afuera) para que el reintento post-401 arranque con uno fresco: un
          // AbortSignal ya disparado aborta el segundo intento al instante.
          ...(isMultipartForm || isLargeUpload ? {} : { signal: timeoutSignal(effectiveTimeoutMs) })
        };

        if (isMultipartForm) delete config.headers['Content-Type'];

        const networkStartedAt = performance.now();
        try {
          const response = await fetch(`${API_URL}/${endpoint}`, config);
          latestResponse = response;
          return response;
        } finally {
          networkDurationMs += performance.now() - networkStartedAt;
        }
      };

      try {
        let response = await doFetch(false).catch(onNetworkError);

        // Un 401 aislado suele ser un access token stale en sesiones largas (Setup 2
        // llegaba a 60-80 min y el PUT del storefront moría acá): ANTES esto
        // deslogueaba al vendor a mitad del setup perdiéndole todo el progreso
        // (setup_2_step_2_failed "Por favor, vuelve a iniciar sesión"). Reintentamos
        // UNA vez forzando un token fresco del refresh token; solo si el 401 persiste
        // (sesión de Auth0 realmente muerta) va al logout.
        if (response.status === 401 && isAuthRequired && !getPerfAccessToken()) {
          retried401 = true;
          try {
            console.warn(`401 en ${endpoint} — reintentando con token fresco`);
            response = await doFetch(true).catch(onNetworkError);
            // Telemetría: ¿el token fresco salvó la sesión o cyclone volvió a rechazar?
            trackEvent('api_401_retry_result', {
              endpoint: sanitizeEndpoint(endpoint),
              recovered: response.status !== 401
            });
          } catch (refreshErr: any) {
            // El refresh mismo falló. ANTES este catch tragaba el motivo en silencio y no
            // podíamos distinguir "refresh token revocado por reuse-detection" (invalid_grant)
            // de "localstorage limpio + iframe bloqueado" (login_required / missing_refresh_token).
            trackEvent('api_401_refresh_failed', {
              endpoint: sanitizeEndpoint(endpoint),
              error: refreshErr?.error || refreshErr?.message || 'unknown'
            });
            // Si el reintento murió por TRANSPORTE (timeout o conexión cortada), la sesión
            // NO está muerta: no sabemos nada de ella. Sin este throw la ejecución seguía
            // de largo hasta el bloque de abajo y deslogueaba al vendor por un cuelgue de
            // red — y encima lo mandaba a re-loguearse por esa misma red. Cuando vence el
            // access token, TODA la ráfaga de queries pega 401 a la vez: alcanza con que
            // una se cuelgue. Relanzamos y que lo maneje quien llamó.
            if (
              refreshErr?.name === 'TimeoutError' ||
              refreshErr?.name === 'TypeError' ||
              refreshErr?.name === 'AbortError'
            ) {
              throw refreshErr;
            }
          }
        }

        // status which does not break the app
        if (response.status === 401) {
          trackEvent('api_401_session_lost', { endpoint: sanitizeEndpoint(endpoint) });
          queryClient.clear();
          logOutUser();
          toast.error('Por favor, vuelve a iniciar sesión.');
          return Promise.reject({ message: 'Por favor, vuelve a iniciar sesión.' });
        }
        // God mode es sólo para cuentas admin: Cyclone responde 403 GOD_MODE_FORBIDDEN a cualquier otra.
        // Un god mode viejo en el navegador (de una prueba, de otra persona) no puede dejar a un negocio
        // sin su propia cuenta: se borra y la app vuelve a cargar como quien inició sesión.
        if (response.status === 403 && vendorUsernameGodMode.storedValue) {
          const denied = await response
            .clone()
            .json()
            .catch(() => null);
          if (denied?.code === 'GOD_MODE_FORBIDDEN') {
            if (!godModeRevoked) {
              godModeRevoked = true;
              try {
                window.localStorage.removeItem('vendorUsernameGodMode');
              } catch {
                // Sin localStorage no hay god mode guardado que borrar.
              }
              toast.error('Esta cuenta no puede ver otros negocios. Volvés a la tuya.');
              window.location.assign('/');
            }
            return Promise.reject({ statusCode: 403, message: 'God mode requires an admin account.' });
          }
        }
        // Solo con `keepErrorMessage`: el texto que mandó el servidor, si es JSON y explica algo.
        // Boom sin mensaje repite el nombre del estado («Not Found»): eso no se muestra.
        const serverMessage = async (): Promise<{ message?: string; code?: unknown }> => {
          if (!keepErrorMessage) return {};
          const payload = await response
            .clone()
            .json()
            .catch(() => null);
          const message = typeof payload?.message === 'string' ? payload.message.trim() : '';
          return message && message !== payload?.error
            ? { message, ...(payload.code !== undefined && { code: payload.code }) }
            : {};
        };
        if (response.status === 404) {
          if (!suppressNotFoundToast) toast.error('Resource not found.');
          // `statusCode` viaja para que quien llama pueda tratar un 404 esperable como una
          // respuesta y no como una falla, sin adivinar por el texto del mensaje.
          return Promise.reject({ statusCode: 404, message: NOT_FOUND_MESSAGE, ...(await serverMessage()) });
        }
        if (response.status >= 500) {
          return Promise.reject({
            statusCode: response.status,
            message: SERVICE_UNAVAILABLE_MESSAGE,
            ...(await serverMessage())
          });
        }

        // Handle 204 No Content (common for DELETE requests)
        if (response.status === 204) {
          return undefined as any;
        }

        const parseStartedAt = performance.now();
        const responseData = await response
          .json()
          .catch(() => {
            return Promise.reject({ message: 'Invalid response' });
          })
          .finally(() => {
            parseDurationMs += performance.now() - parseStartedAt;
          });

        if (response.ok) {
          return responseData;
        }
        return Promise.reject(responseData);
      } catch (error: any) {
        if (!latestResponse) errorName = error?.name || error?.constructor?.name || 'Error';
        if (error.name === 'AbortError') {
          console.debug('Request was aborted during execution');
          throw error;
        }
        throw error;
      } finally {
        document.removeEventListener('visibilitychange', markPageHidden);
        recordApiPerformance({
          endpoint,
          method: type,
          durationMs: performance.now() - requestStartedAt,
          authDurationMs,
          networkDurationMs,
          parseDurationMs,
          response: latestResponse,
          retried401,
          errorName,
          pageVisibilityAtStart,
          pageVisibilityAtEnd: document.visibilityState,
          pageHiddenDuringRequest
        });
      }
    },
    [getAccessTokenSilently, logOutUser, queryClient, vendorUsernameGodMode.storedValue]
  );

  return useMemo(() => ({ client }), [client]);
};
